/**
 * OrgSwitcher — dropdown for multi-org users to switch organizations.
 * Visible to csuite + platform-ops only (#443). Everyone else — reps,
 * supervisors, managers, admins, auditors, agency owners — stays on
 * whichever org their active membership resolves to. Calls POST /auth/switch-org.
 */

import { useState, useRef, useEffect } from 'react';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { useAuth } from '../../providers/AuthProvider';

export function OrgSwitcher() {
  const { user, switchOrg } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const orgs = user?.organizations;
  const allowed = user?.role?.toLowerCase() === 'csuite' || !!user?.platformOps;
  if (!allowed) return null;
  if (!orgs || orgs.length <= 1) return null;

  const activeOrg = orgs.find((o) => o.id === user?.activeOrgId);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-1.5 bg-secondary/30 hover:bg-secondary/60 border border-border/60 rounded-lg transition-colors cursor-pointer shadow-sm text-[0.75rem] font-medium text-foreground min-w-[140px] justify-between"
      >
        <div className="flex items-center gap-1.5 truncate">
          <Building2 className="w-3.5 h-3.5 text-muted-foreground stroke-[1.5] flex-shrink-0" />
          <span className="truncate">{activeOrg?.name || 'Select org'}</span>
        </div>
        <ChevronDown
          className={`w-3.5 h-3.5 text-muted-foreground transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1.5 w-[220px] bg-card border border-border/80 rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] z-50 overflow-hidden">
          <div className="py-1">
            <div className="px-3 py-1.5 text-[0.625rem] font-bold text-muted-foreground uppercase tracking-wider">
              Organizations
            </div>
            {orgs.map((org) => (
              <button
                key={org.id}
                onClick={() => {
                  setOpen(false);
                  if (org.id !== user?.activeOrgId) {
                    switchOrg(org.id);
                  }
                }}
                className={`w-full flex items-center justify-between px-3 py-2 text-[0.75rem] hover:bg-secondary transition-colors cursor-pointer ${
                  org.id === user?.activeOrgId ? 'bg-primary/5 font-medium' : ''
                }`}
              >
                <span className="truncate">{org.name}</span>
                {org.id === user?.activeOrgId && (
                  <Check className="w-3.5 h-3.5 text-primary stroke-[2] flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
